import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Loader2, Edit, ExternalLink } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

type DuplicadoBy = 'sku' | 'name'

interface DuplicadoProducto {
  id: string
  sku: string
  nombre: string
  precio: number
  stock: number
  imagen: string
  brandNombre: string
  categoriaNombre: string
}

interface DuplicadosResponse {
  groups: Array<{
    key: string
    products: DuplicadoProducto[]
  }>
}

function stockBadge(stock: number) {
  if (stock === 0) return <Badge variant="danger">Agotado</Badge>
  if (stock < 5) return <Badge variant="warning">Stock bajo ({stock})</Badge>
  return <span className="text-sm font-medium text-ink-700">{stock}</span>
}

export function ProductosDuplicados() {
  const navigate = useNavigate()
  const [by, setBy] = useState<DuplicadoBy>('sku')

  const { data, isLoading, isFetching } = useQuery<DuplicadosResponse>({
    queryKey: ['productos-duplicados', by],
    queryFn: () => apiGet(`/api/admin/productos/duplicados?by=${by}`),
    placeholderData: (prev) => prev,
  })

  function goToEdit(id: string) {
    navigate({ to: '/productos/$id/editar', params: { id } })
  }

  function goToDetail(id: string) {
    navigate({ to: '/productos/$id', params: { id } })
  }

  const groups = data?.groups ?? []
  const totalGroups = groups.length
  const totalProducts = groups.reduce((acc, g) => acc + g.products.length, 0)

  return (
    <>
      <Topbar
        title="Productos duplicados"
        subtitle={
          data
            ? `${totalGroups} grupo(s) — ${totalProducts} producto(s) coincidentes`
            : 'Cargando…'
        }
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-700">Detectar por:</span>
          <label className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50">
            <input
              type="radio"
              name="duplicados-by"
              value="sku"
              checked={by === 'sku'}
              onChange={() => setBy('sku')}
              className="size-3.5 text-brand-600 focus:ring-brand-500"
            />
            SKU
          </label>
          <label className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50">
            <input
              type="radio"
              name="duplicados-by"
              value="name"
              checked={by === 'name'}
              onChange={() => setBy('name')}
              className="size-3.5 text-brand-600 focus:ring-brand-500"
            />
            Nombre
          </label>
          {isFetching && !isLoading && (
            <Loader2 className="size-4 animate-spin text-brand-600" />
          )}
        </div>

        {isLoading && (
          <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Buscando duplicados…</p>
          </div>
        )}

        {!isLoading && groups.length === 0 && (
          <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-16 text-ink-500">
            <p className="text-sm">
              No se encontraron productos duplicados por {by === 'sku' ? 'SKU' : 'nombre'}.
            </p>
          </div>
        )}

        {groups.length > 0 && (
          <div className="space-y-4">
            {groups.map((group) => (
              <div
                key={group.key}
                className="overflow-hidden rounded-2xl border border-ink-200 bg-white"
              >
                <div className="flex items-center justify-between border-b border-ink-200 bg-ink-50 px-4 py-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-500">
                      {by === 'sku' ? 'SKU duplicado' : 'Nombre duplicado'}
                    </div>
                    <div
                      className={`mt-0.5 font-semibold text-ink-900 ${
                        by === 'sku' ? 'font-mono text-sm' : 'text-base'
                      }`}
                    >
                      {group.key}
                    </div>
                  </div>
                  <Badge variant="warning">{group.products.length} coincidencias</Badge>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white">
                      <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                        <th className="px-4 py-2 font-medium">Producto</th>
                        <th className="px-4 py-2 font-medium">SKU</th>
                        <th className="px-4 py-2 font-medium">Categoría / Marca</th>
                        <th className="px-4 py-2 font-medium">Stock</th>
                        <th className="px-4 py-2 font-medium text-right">Precio</th>
                        <th className="w-28 px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.products.map((p) => (
                        <tr key={p.id} className="border-t border-ink-100 hover:bg-ink-50">
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-3">
                              <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-ink-100 text-xs font-bold text-brand-600">
                                {p.imagen ? (
                                  <img
                                    src={p.imagen}
                                    alt=""
                                    className="size-9 rounded-lg object-cover"
                                    onError={(e) => {
                                      ;(e.currentTarget as HTMLImageElement).style.display =
                                        'none'
                                    }}
                                  />
                                ) : (
                                  'M'
                                )}
                              </div>
                              <div className="line-clamp-2 max-w-md font-medium text-ink-900">
                                {p.nombre}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-ink-700">{p.sku}</td>
                          <td className="px-4 py-2">
                            <div className="text-xs text-ink-700">
                              {p.categoriaNombre || '—'}
                            </div>
                            <div className="text-xs text-ink-500">{p.brandNombre || '—'}</div>
                          </td>
                          <td className="px-4 py-2">{stockBadge(p.stock)}</td>
                          <td className="px-4 py-2 text-right font-semibold text-ink-900">
                            {formatMXN(p.precio)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => goToDetail(p.id)}
                                className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                title="Ver detalle"
                              >
                                <ExternalLink className="size-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => goToEdit(p.id)}
                                className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100"
                                title="Editar"
                              >
                                <Edit className="size-3.5" /> Editar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default ProductosDuplicados
